import { type OperationResult } from 'urql';

import type {
  DeliveryOrderDetailInput,
  Exact,
  SubmitOrderUsingBillingAccountInput,
  SubmitOrderUsingBillingAccountMutation,
  SubmitOrderUsingBillingAccountResponseUnion,
  SubmitOrderUsingPaymentMethodCardInput,
  SubmitOrderUsingPaymentMethodCardMutation,
  SubmitOrderWithPaymentMethodResponseUnion,
  ValidateRewardFailureCode,
} from '@order/graphql';
import { getUrqlError } from '@order/utils';

import type { PaymentMethodType } from '../../Checkout.utils';

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function processSubmitResponse(
  submitResponse:
    | SubmitWithBillingAccountResponse
    | SubmitWithPaymentMethodCardResponse,
) {
  const billingAccountResponse = (
    submitResponse as SubmitWithBillingAccountResponse
  )?.data?.submitOrderUsingBillingAccount;

  const paymentMethodCardResponse = (
    submitResponse as SubmitWithPaymentMethodCardResponse
  )?.data?.submitOrderUsingPaymentMethodCard;

  const data = billingAccountResponse ?? paymentMethodCardResponse;

  const typename = data?.__typename ?? 'ValidationError';
  const isSuccess = /SubmitOrder.*Success/.test(typename as string);
  const userError = (data as Readonly<{ message: string }>)?.message ?? '';
  const systemError = getUrqlError(submitResponse.error);

  return {
    typename,
    isSuccess,
    userError,
    systemError,
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

export const VALIDATION_ERROR: CheckoutOutput = {
  typename: 'ValidationError',
  isSuccess: false,
  userError: '',
  systemError: '',
};

// ─── Types ───────────────────────────────────────────────────────────────────

export type SubmitWithBillingAccountResponse = OperationResult<
  SubmitOrderUsingBillingAccountMutation,
  Exact<{
    input: SubmitOrderUsingBillingAccountInput;
  }>
>;

export type SubmitWithPaymentMethodCardResponse = OperationResult<
  SubmitOrderUsingPaymentMethodCardMutation,
  Exact<{
    input: SubmitOrderUsingPaymentMethodCardInput;
  }>
>;

export type CheckoutInput = Readonly<{
  orderId: string;
  wantedTime: string;
  paymentMethodId: string;
  paymentMethodType: PaymentMethodType;
  useCredit: boolean;
  dropoffLocationId: string | null;
  contactNumber: string | null;
  includeUtensils: boolean;
  deliveryOrderDetail?: DeliveryOrderDetailInput | null;
}>;

export type CheckoutOutput = Readonly<{
  typename:
    | SubmitOrderUsingBillingAccountResponseUnion['__typename']
    | SubmitOrderWithPaymentMethodResponseUnion['__typename'];
  isSuccess: boolean;
  userError: string;
  systemError: string;
}>;

export type SubmitOrderMutationResponse = Readonly<{
  isSubmittingOrder: boolean;
  rewardFailureCode: ValidateRewardFailureCode | undefined;
  submitOrderMutation: (input: CheckoutInput) => Promise<CheckoutOutput>;
}>;
